import {
  callApi,
  NetworkError,
  type CallApiInput,
  type Endpoint,
  ApiError,
} from '@groundly/shared';
import type { z } from 'zod';

/**
 * Every server call goes through the shared typed client
 * (`patterns/api-conventions.md`, 2): no hand-written URLs and no bare `fetch`.
 * Empty means the same origin as the dashboard, which is the local and
 * production shape.
 */
export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';

export function call<E extends Endpoint>(
  endpoint: E,
  input: CallApiInput<E> = {},
): Promise<z.output<E['response']>> {
  return callApi(endpoint, input, { baseUrl: API_BASE_URL });
}

/** No session, or it has expired (`patterns/error-handling.md`, 2). */
export function isUnauthenticated(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'UNAUTHENTICATED';
}

/** The Account User lacks the endpoint's permission (`patterns/error-handling.md`, 2). */
export function isPermissionDenied(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'PERMISSION_DENIED';
}

export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'NOT_FOUND';
}

/**
 * The request never reached Groundly. Screens say something different for this
 * than for a server error (`ux.md`, States).
 */
export function isNoResponse(error: unknown): boolean {
  return error instanceof NetworkError;
}
